import kebabCase from 'lodash/kebabCase';
import head from 'lodash/head';
import PropTypes from 'prop-types';
import React, {useEffect, useRef} from 'react';
import {
  expanded,
  hidden,
  menuToggle,
  selected,
  subtopic,
  subtopicLink,
  subtopics,
  topicsStage,
  topicName,
  topicNav,
  topics as topicsStyle,
} from 'src/pages/category/components/style.module.scss';
import {Accordion} from 'src/shared/components/Accordion';
import {Link} from 'src/shared/components/Link';

const DOWN = 40;
const UP = 38;

export const Topics = ({
  onChangeTopics,
  selectedSubtopic,
  selectedTopic,
  topics,
}) => {
  const articleSectionRef = useRef(null);

  const isSelectedTopic = topic => topic === selectedTopic;

  const changeTab = e => {
    const isDirectionPressed = (e.keyCode === UP || e.keyCode === DOWN);
    if (!isDirectionPressed) {
      return;
    }
    if (e.keyCode === UP) {
      e.target.previousElementSibling?.focus();
    }
    if (e.keyCode === DOWN) {
      e.target.nextElementSibling?.focus();
    }
    e.preventDefault();
  };

  useEffect(() => {
    setTimeout(() => {
      if (topics && topics.length && selectedTopic) {
        // selected topic is not the first available topic in the list of topics
        if (head(topics).name !== selectedTopic.name) {
          articleSectionRef.current.scrollIntoView();
        }
      }
    }, 100);
  }, []);

  return (
    <div aria-live="polite" className={topicsStyle} role="tablist" aria-orientation="vertical" aria-busy="true" aria-label="Articles">
      <div className={`${topicsStage} ${selectedTopic ? expanded : ''}`}>
        <div className={topicNav}>
          {
            topics.map(topic => (
              <button
                key={topic.name}
                id={kebabCase(topic.name)}
                role="tab"
                aria-selected={topic.isSelected}
                aria-controls={kebabCase(topic.name + 'TabPanel')}
                tabIndex={isSelectedTopic(topic) ? '0' : '-1'}
                onClick={() => onChangeTopics(topic)}
                onKeyDown={changeTab}
                className={isSelectedTopic(topic) ? selected : ''}
                type="button"
              >
                {topic.name}
              </button>
            ))
          }
        </div>

        <div className={subtopics} ref={articleSectionRef} id="ArticleSubtopics">
          <button className={menuToggle} onClick={() => onChangeTopics()}>Back</button>
          {
            topics.map(topic => (
              <div
                role="tabpanel"
                tabIndex={isSelectedTopic(topic) ? '0' : '-1'}
                key={`${topic.name}TabPanel`}
                id={kebabCase(topic.name + 'TabPanel')}
                aria-labelledby={kebabCase(topic.name)}
                hidden={topic !== selectedTopic}
                aria-hidden={topic !== selectedTopic}
                className={`${subtopic} ${topic !== selectedTopic ? hidden : ''}`}
              >
                <h2 className={topicName}>{topic.name}</h2>
                {
                  topic.subtopics
                    .filter(subtopic => subtopic.links.length > 0)
                    .map(subtopic => (
                      <Accordion
                        key={subtopic.name}
                        expanded={selectedSubtopic === subtopic}
                        descriptiveLabel={`${subtopic.name} ${subtopic.links.length} Articles`}
                        tabIndex={topic !== selectedTopic ? -1 : undefined}
                        triggerLabel={<div className={subtopicLink}>{subtopic.name} <span>{subtopic.links.length} Articles</span></div>}
                        onToggle={() => onChangeTopics(topic, selectedSubtopic === subtopic ? undefined : subtopic)}
                      >
                        <ul>
                          {
                            subtopic.links.map(link => (
                              <li key={`${link.text}${link.href}`}>
                                <Link {...link} tabIndex={isSelectedTopic(topic) ? undefined : -1} />
                              </li>
                            ))
                          }
                        </ul>
                      </Accordion>
                    ))
                }
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
};

Topics.propTypes = {
  onChangeTopics: PropTypes.func.isRequired,
  selectedSubtopic: PropTypes.object,
  selectedTopic: PropTypes.object,
  topics: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    subtopics: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      links: PropTypes.arrayOf(PropTypes.shape({
        href: PropTypes.string.isRequired,
        target: PropTypes.oneOf(['_self', '_blank', '_top']),
        text: PropTypes.string.isRequired,
      })),
    })),
  })),
};
